import {memo, useEffect, useRef} from 'react';

import styled from 'styled-components';

import {RestaurantFromGet} from '~/shared/store/models';
import {MutatedFutureOrderAvailableDatesAndTimes} from '~/shared/store/models/FutureOrderAvailableDatesAndTimes';
import {body12Bold, body14Normal} from '~/shared/theme/typography';
import {PrimaryActionButton} from '~/shared/components/Button';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {isCurrentTimeKeyAsap} from '~/shared/utils/general';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR} from '~/shared/theme/utils';
import LogoIcon from '~/assets/images/icons/10bisPlusIcon.svg?react';
import {flexCenter} from '~/shared/theme/FlexLayout';
import {convertGapToMargin} from '~/shared/theme/convertGapToMargin';

const Root = styled(ClickOutsideDiv)<{className?: string}>`
  position: absolute;
  ${flipOnLTR`left: 10px;`}
  width: calc(100% - 22px);
  z-index: 2;
  background-color: ${({theme}) => theme.colors.surface};
  border-radius: 4px;
  border: 1px solid ${({theme}) => theme.colors.gray500};
  box-shadow: ${({theme}) => theme.shadows.shadow1};
`;

const Content = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 240px;
`;

const TimeRow = styled(PrimaryActionButton)<{isSelected?: boolean}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 20px;
  width: 100%;
  background-color: ${({theme, isSelected}) => (isSelected ? theme.colors.gray900 : theme.colors.surface)};
  margin: 0;
  ${({theme}) => convertGapToMargin('0 6px', theme.isLTR)};

  &:hover {
    background-color: ${({theme}) => theme.colors.gray900};
    border: none;
  }
`;

const FreeDeliveryLabel = styled.div`
  ${flexCenter}
  ${body12Bold}
  padding: 2px 4px;
  border-radius: 4px;
  color: ${({theme}) => theme.tagColorsForeground.NoDeliveryFee};
  background-color: ${({theme}) => theme.tagColorsBackground.NoDeliveryFee};
  ${flipOnLTR`
    margin-right: 4px;
  `}
`;

const Time = styled.div`
  line-height: 24px;
  color: ${({theme}) => theme.colors.text2};
  ${body14Normal}
`;

const StyledLogoIcon = styled(LogoIcon)`
  margin-bottom: 2px;
  ${flipOnLTR`
    margin-left: 4px;
  `}
`;

interface TimeDropdownProps {
  futureOrderAvailableDatesAndTimes: MutatedFutureOrderAvailableDatesAndTimes[];
  deliveryRules?: RestaurantFromGet['deliveryRules'];
  currentTimeKey: string;
  deliveryTimeRange: string;
  onClick: (times: MutatedFutureOrderAvailableDatesAndTimes['times'][number], newDeliverFee?: number) => void;
  onClickOutside: () => void;
  className?: string;
  isBenefitsHighlightEnabled?: boolean;
}

const TimeDropdown = ({
  className,
  futureOrderAvailableDatesAndTimes,
  deliveryRules,
  currentTimeKey,
  deliveryTimeRange,
  onClick,
  onClickOutside,
  isBenefitsHighlightEnabled,
}: TimeDropdownProps) => {
  const {t} = getLocalizationService();
  const currentItemRef = useRef<HTMLButtonElement>(null);

  const handleClickOutside = (e: Event) => {
    if (
      e.target instanceof Element &&
      (e.target.classList.contains('toggle_sdfo_dropdown') || (e.target?.nodeName === 'path' && e.target?.parentElement?.classList?.contains('toggle_sdfo_dropdown')))
    ) {
      return;
    }
    onClickOutside();
  };

  useEffect(() => {
    if (currentItemRef.current) {
      currentItemRef.current.scrollIntoView({behavior: 'instant', block: 'nearest'});
    }
  }, []);

  return (
    <Root onClickOutside={handleClickOutside} className={className}>
      <Content>
        {futureOrderAvailableDatesAndTimes.map(({times}) => {
          return times.map(time => {
            const {timeKeyLabel, ruleId, timeKey, timeRange} = time;
            const rule = deliveryRules?.find(currentDeliveryRule => currentDeliveryRule.id === ruleId);
            const isAsap = isCurrentTimeKeyAsap(timeKey);
            const isSelected = currentTimeKey && currentTimeKey === timeKey;
            return (
              <TimeRow
                onClick={() => onClick({
                timeKey,
                timeKeyLabel: isAsap ? deliveryTimeRange : timeKeyLabel,
                timeRange,
                ruleId,
                }, rule?.deliveryFee)}
                key={timeKey}
                isSelected={isSelected}
                ref={isSelected ? currentItemRef : null}
              >
                <Time>{isAsap ? deliveryTimeRange : timeKeyLabel}</Time>
                {!rule?.deliveryFee && (
                  <FreeDeliveryLabel>
                    {isBenefitsHighlightEnabled && <StyledLogoIcon height={11} width={11} viewBox="0 0 12 12" />}
                    {t('free_delivery')}
                  </FreeDeliveryLabel>
                )}
              </TimeRow>
            );
          });
        })}
      </Content>
    </Root>
  );
};

export default memo(TimeDropdown);
